import { useMediaQuery, useResizeObserver } from '@mantine/hooks';
import { useMotionValue, useSpring } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import { useAgentTraining } from '@/store/agent-training.ts';
import styles from './StepsLoader.module.css';

const VISIBLE_ROWS = 3;
const MINI_INITIAL_LOADING = 3;
const GAP = 12;

export const useStepsLoaderAnimator = () => {
	const { isMiniCreatorMode } = useHostContext();
	const { scrapingSteps } = useAgentTraining();
	const prevMode = useRef(isMiniCreatorMode);
	const realCount = scrapingSteps.filter((s) => !s.isLoading).length;
	const hasInitialPositioned = useRef(false);

	const isSm = useMediaQuery('(min-width: 640px)');
	const isXl = useMediaQuery('(min-width: 1280px)');
	let cols = 1;

	if (!isMiniCreatorMode) {
		if (isXl) {
			cols = 3;
		} else if (isSm) {
			cols = 2;
		}
	}

	const totalRows = Math.ceil(realCount / cols);
	const initialRows = VISIBLE_ROWS;

	const [cardRef, cardRect] = useResizeObserver();
	const cardWidth = cardRect?.width || 0;

	const y = useMotionValue(0);
	const x = useMotionValue(0);

	const smoothX = useSpring(x, { stiffness: 60, damping: 18 });
	const smoothY = useSpring(y, { stiffness: 60, damping: 18 });

	const prevRealCount = useRef(realCount);
	const completedRowsAfterInitial = useRef(
		Math.max(0, Math.ceil(realCount / cols) - VISIBLE_ROWS)
	);
	const animatedNodes = useRef(new Set<string>());

	const rowHeight = useMemo(() => {
		return (cardRect?.height || 160) + 20; // 20 = CSS gap-[20px] between grid rows
	}, [cardRect?.height]);

	const reset = useCallback(() => {
		prevRealCount.current = 0;
		completedRowsAfterInitial.current = 0;
		hasInitialPositioned.current = false;
		x.set(0);
		y.set(0);
		animatedNodes.current.clear();
	}, [x, y]);

	const animateOnMountRef = useCallback((el: HTMLDivElement | null) => {
		if (!el) {
			return;
		}

		const id = el.dataset.id ?? '';
		if (animatedNodes.current.has(id)) {
			return;
		}

		animatedNodes.current.add(id);
		el.classList.add(styles.animatedContainer);

		el.addEventListener(
			'animationend',
			() => el.classList.remove(styles.animatedContainer),
			{ once: true }
		);
	}, []);

	useEffect(() => {
		if (!cardWidth || hasInitialPositioned.current) {
			return;
		}

		const STEP = cardWidth + GAP;

		if (isMiniCreatorMode) {
			const totalCards = realCount + 1;
			const visible = 3;
			const scrollCount = Math.max(0, totalCards - visible);

			if (realCount >= MINI_INITIAL_LOADING) {
				x.set(-(scrollCount * STEP));
			}
		} else if (totalRows > initialRows) {
			y.set(-((totalRows - initialRows) * rowHeight));
		}

		hasInitialPositioned.current = true;
	}, [
		cardWidth,
		isMiniCreatorMode,
		realCount,
		totalRows,
		initialRows,
		x,
		y,
		rowHeight
	]);

	useEffect(() => {
		if (prevMode.current !== isMiniCreatorMode) {
			hasInitialPositioned.current = false;
			prevMode.current = isMiniCreatorMode;
		}
	}, [isMiniCreatorMode]);

	// Re-sync scroll position after layout changes and real-count updates.
	useEffect(() => {
		if (!hasInitialPositioned.current || !cardWidth) {
			return;
		}

		if (isMiniCreatorMode) {
			const scrollCount = Math.max(0, realCount + 1 - 3);
			if (realCount >= MINI_INITIAL_LOADING) {
				x.set(-(scrollCount * (cardWidth + GAP)));
			}
		} else {
			const correctRows = Math.max(
				0,
				Math.ceil(realCount / cols) - VISIBLE_ROWS
			);
			if (correctRows !== completedRowsAfterInitial.current) {
				completedRowsAfterInitial.current = correctRows;
				y.set(-(correctRows * rowHeight));
			}
		}
	}, [realCount, cols, rowHeight, cardWidth, isMiniCreatorMode, x, y]);

	return {
		containerStyle: isMiniCreatorMode ? { x: smoothX } : { y: smoothY },
		reset,
		animateOnMountRef,
		cardRef,
		cols
	};
};
